import env from '@config/env';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { adminAuthApi, adminUsersApi, authApi, merchantAuthApi } from '@services/api';
import encryptedStorage from '@storage/encryptedStorage';
import { STORAGE_KEYS } from '@storage/storageKeys';
import { getApiErrorMessage } from '@utils/apiError';
import {
  extractAuthTokens,
  getAccessToken,
  getRefreshToken,
  getUserIdFromToken,
  resolveMustChangePassword,
} from '@utils/authTokens';
import { removeCookie, setCookie } from '@utils/cookie';
import { toMerchantOtpPayload, toMerchantVerifyOtpPayload } from '@utils/formatter';

const ACCESS_TOKEN_COOKIE = env.TOKEN_KEY;
const LEGACY_ACCESS_TOKEN_COOKIE = 'accessToken';

const persistRefreshToken = (refreshToken) => {
  if (!refreshToken) return;
  encryptedStorage.setItem(env.REFRESH_TOKEN_KEY, refreshToken);
  setCookie(env.REFRESH_TOKEN_KEY, refreshToken, 60 * 60 * 24 * 30);
};

const clearRefreshToken = () => {
  encryptedStorage.removeItem(env.REFRESH_TOKEN_KEY);
  removeCookie(env.REFRESH_TOKEN_KEY);
};

const persistVerifyOtpSession = (payload) => {
  if (payload == null) return;

  encryptedStorage.setItem(STORAGE_KEYS.VERIFY_OTP_RESPONSE, payload);

  const { accessToken, refreshToken, data } = extractAuthTokens(payload);
  const token = accessToken || data.accessToken || data.token || null;
  const userId = data.userId || data.id || (token ? getUserIdFromToken(token) : null) || null;

  if (userId) {
    encryptedStorage.setItem(STORAGE_KEYS.ENCRYPTION_USER_ID, String(userId));
  }
  if (refreshToken) {
    persistRefreshToken(refreshToken);
  }
};

const clearVerifyOtpSession = () => {
  encryptedStorage.removeItem(STORAGE_KEYS.VERIFY_OTP_RESPONSE);
  encryptedStorage.removeItem(STORAGE_KEYS.ENCRYPTION_USER_ID);
};

const persistAccessToken = (accessToken, expiresInMs) => {
  if (!accessToken) return;

  const maxAgeSeconds =
    Number.isFinite(Number(expiresInMs)) && Number(expiresInMs) > 0
      ? Math.floor(Number(expiresInMs) / 1000)
      : 60 * 60 * 24;

  // Persist under TOKEN_KEY from .env.admin / .env.merchant.
  // Cookie name must be portal-specific: cookies on localhost are shared
  // across ports, so a shared `accessToken` cookie overwrites the other portal.
  setCookie(ACCESS_TOKEN_COOKIE, accessToken, maxAgeSeconds);
  removeCookie(LEGACY_ACCESS_TOKEN_COOKIE);
  encryptedStorage.setItem(env.TOKEN_KEY, accessToken);
};

const clearAccessToken = () => {
  removeCookie(ACCESS_TOKEN_COOKIE);
  removeCookie(LEGACY_ACCESS_TOKEN_COOKIE);
  encryptedStorage.removeItem(env.TOKEN_KEY);
};

const getStoredAccessToken = () => getAccessToken();

const getStoredMustChangePassword = () => {
  if (encryptedStorage.getItem('mustChangePassword')) return true;
  if (encryptedStorage.getItem('user')?.mustChangePassword) return true;
  return resolveMustChangePassword({}, getStoredAccessToken());
};

const persistMustChangePassword = (state, value) => {
  const mustChangePassword = Boolean(value);
  state.mustChangePassword = mustChangePassword;
  if (state.user) {
    state.user.mustChangePassword = mustChangePassword;
    encryptedStorage.setItem('user', state.user);
  }
  if (mustChangePassword) {
    encryptedStorage.setItem('mustChangePassword', true);
  } else {
    encryptedStorage.removeItem('mustChangePassword');
  }
};

const persistAuthSession = (state, payload, { mustChangePassword } = {}) => {
  const { accessToken, refreshToken, expiresIn, data } = extractAuthTokens(payload);
  const nextMustChangePassword =
    mustChangePassword !== undefined
      ? Boolean(mustChangePassword)
      : resolveMustChangePassword(payload, accessToken);
  const user = mapAuthUser({
    ...(state.user || {}),
    ...(data && typeof data === 'object' ? data : {}),
    mustChangePassword: nextMustChangePassword,
  });

  state.user = user;
  if (accessToken) {
    state.token = accessToken;
    persistAccessToken(accessToken, expiresIn);
  }
  if (refreshToken) {
    state.refreshToken = refreshToken;
    persistRefreshToken(refreshToken);
  }
  state.isAuthenticated = !!(state.token || accessToken);
  encryptedStorage.setItem('user', user);
  persistMustChangePassword(state, nextMustChangePassword);
};

const mapAuthUser = (data = {}) => {
  const source =
    data.user && typeof data.user === 'object' && !Array.isArray(data.user)
      ? { ...data, ...data.user }
      : data;
  const firstName = source.firstName || '';
  const lastName = source.lastName || '';
  const email = source.email || '';

  return {
    id: source.userId || source.id,
    email,
    phone: source.phoneNumber || source.phone || '',
    firstName,
    lastName,
    name: `${firstName} ${lastName}`.trim() || source.phoneNumber || email || '',
    role: source.role || source.userType || 'merchant',
    userType: source.userType || 'MERCHANT',
    profilePicUrl: source.profilePicUrl || '',
    hasApprovedBusiness: Boolean(source.hasApprovedBusiness),
    isNewUser: Boolean(source.isNewUser),
    mustChangePassword: Boolean(source.mustChangePassword),
    permissions: source.permissions || [
      'dashboard.view',
      'profile.view',
      'settings.view',
      'users.view',
    ],
    roles: source.roles || [source.role || 'merchant'],
  };
};

export const mapMerchantProfile = (data = {}) => {
  const mapped = mapAuthUser(data);
  return {
    ...mapped,
    phone: data.phoneNumber || data.phone || mapped.phone || '',
    profilePicUrl:
      data.profilePicUrl || data.profileImageUrl || data.avatar || mapped.profilePicUrl,
  };
};

export const loginUser = createAsyncThunk(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response =
        env.APP_TYPE === 'admin'
          ? await adminAuthApi.login(credentials)
          : await authApi.login(credentials);
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Login failed'));
    }
  },
);

export const sendMerchantOtp = createAsyncThunk(
  'auth/sendMerchantOtp',
  async (phone, { rejectWithValue }) => {
    try {
      const response = await merchantAuthApi.sendOtp(toMerchantOtpPayload(phone));
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to send OTP'));
    }
  },
);

export const resendMerchantOtp = createAsyncThunk(
  'auth/resendMerchantOtp',
  async (phone, { rejectWithValue }) => {
    try {
      const response = await merchantAuthApi.resendOtp(toMerchantOtpPayload(phone));
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to send OTP'));
    }
  },
);

export const verifyOtp = createAsyncThunk(
  'auth/verifyOtp',
  async ({ otp }, { rejectWithValue, getState }) => {
    const phone = getState().auth.pendingPhone;
    try {
      const response = await merchantAuthApi.verifyOtp(toMerchantVerifyOtpPayload(phone, otp));
      persistVerifyOtpSession(response.data);
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'OTP verification failed'));
    }
  },
);

export const fetchProfile = createAsyncThunk(
  'auth/fetchProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authApi.getProfile();
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch profile'));
    }
  },
);

export const fetchMerchantProfile = createAsyncThunk(
  'auth/fetchMerchantProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await merchantAuthApi.getProfile();
      return response.data?.data ?? response.data ?? null;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to load profile'));
    }
  },
);

const extractProfileDetails = (response) => {
  const root = response?.data ?? response ?? {};
  const nested = root?.data && typeof root.data === 'object' ? root.data : root;
  if (!nested || typeof nested !== 'object' || Array.isArray(nested)) return null;
  return nested.user && typeof nested.user === 'object' ? { ...nested, ...nested.user } : nested;
};

export const fetchAdminProfile = createAsyncThunk(
  'auth/fetchAdminProfile',
  async (_, { getState, rejectWithValue }) => {
    try {
      const userId = String(getState().auth.user?.id || '').trim();
      const response = await adminUsersApi.getProfileDetails(userId ? { userId } : {});
      return extractProfileDetails(response);
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to load profile'));
    }
  },
);

export const logoutUser = createAsyncThunk('auth/logout', async () => {
  try {
    await authApi.logout({ deviceId: '' });
  } catch {
    // Always clear local session even if logout API fails
  }
});

export const changeAdminPassword = createAsyncThunk(
  'auth/changeAdminPassword',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await adminAuthApi.changePassword(payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to reset password'));
    }
  },
);

const applyMerchantOtpSent = (state, action) => {
  const phone = action.meta.arg == null ? null : String(action.meta.arg);
  const payload = action.payload?.data || action.payload;
  const cooldown = Number(payload?.cooldownSeconds);

  state.pendingPhone = phone;
  state.otpCooldownSeconds = Number.isFinite(cooldown) && cooldown > 0 ? cooldown : 30;

  if (phone) {
    encryptedStorage.setItem('pendingPhone', phone);
  }
};

const getInitialState = () => {
  const token = getStoredAccessToken();
  const user = encryptedStorage.getItem('user') || null;
  const refreshToken = getRefreshToken();

  // Cookie fallback: write the token back so later reads hit encrypted storage.
  if (token && !encryptedStorage.getItem(env.TOKEN_KEY)) {
    persistAccessToken(token);
  }
  if (refreshToken && !encryptedStorage.getItem(env.REFRESH_TOKEN_KEY)) {
    persistRefreshToken(refreshToken);
  }

  return {
    user,
    token,
    refreshToken,
    isAuthenticated: !!(token || user),
    mustChangePassword: getStoredMustChangePassword(),
    needsOnboarding: !!encryptedStorage.getItem('needsOnboarding'),
    pendingPhone: (() => {
      const phone = encryptedStorage.getItem('pendingPhone');
      return phone == null ? null : String(phone);
    })(),
    otpCooldownSeconds: 30,
    loading: false,
    error: null,
    sessionExpiry: null,
  };
};

const authSlice = createSlice({
  name: 'auth',
  initialState: getInitialState(),
  reducers: {
    setTokens: (state, action) => {
      const { token, refreshToken, expiresIn } = action.payload;
      state.token = token;
      state.refreshToken = refreshToken;
      state.isAuthenticated = !!token;
      if (token) {
        persistAccessToken(token, expiresIn);
      }
      if (refreshToken) {
        persistRefreshToken(refreshToken);
      }
    },
    setUser: (state, action) => {
      state.user = action.payload;
      encryptedStorage.setItem('user', action.payload);
      if (action.payload?.mustChangePassword !== undefined) {
        persistMustChangePassword(state, action.payload.mustChangePassword);
      }
    },
    setPendingPhone: (state, action) => {
      const phone = action.payload == null ? null : String(action.payload);
      state.pendingPhone = phone;
      if (phone) {
        encryptedStorage.setItem('pendingPhone', phone);
      } else {
        encryptedStorage.removeItem('pendingPhone');
      }
    },
    clearPendingPhone: (state) => {
      state.pendingPhone = null;
      state.otpCooldownSeconds = 30;
      encryptedStorage.removeItem('pendingPhone');
    },
    completeOnboarding: (state) => {
      state.needsOnboarding = false;
      encryptedStorage.removeItem('needsOnboarding');
    },
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.mustChangePassword = false;
      state.needsOnboarding = false;
      state.pendingPhone = null;
      state.otpCooldownSeconds = 30;
      state.error = null;
      state.sessionExpiry = null;
      clearAccessToken();
      clearRefreshToken();
      encryptedStorage.removeItem('user');
      encryptedStorage.removeItem('mustChangePassword');
      encryptedStorage.removeItem('needsOnboarding');
      encryptedStorage.removeItem('pendingPhone');
      clearVerifyOtpSession();
    },
    clearAuthError: (state) => {
      state.error = null;
    },
    setSessionExpiry: (state, action) => {
      state.sessionExpiry = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        persistAuthSession(state, action.payload);
      })
      .addCase(changeAdminPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(changeAdminPassword.fulfilled, (state, action) => {
        state.loading = false;
        persistAuthSession(state, action.payload, { mustChangePassword: false });
      })
      .addCase(changeAdminPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(sendMerchantOtp.fulfilled, applyMerchantOtpSent)
      .addCase(resendMerchantOtp.fulfilled, applyMerchantOtpSent)
      .addCase(verifyOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.loading = false;
        const { accessToken, refreshToken, expiresIn, data } = extractAuthTokens(action.payload);
        const user = mapAuthUser(data);

        state.user = user;
        state.token = accessToken || null;
        state.refreshToken = refreshToken || null;
        state.isAuthenticated = !!accessToken;
        // New users start onboarding; returning approved merchants skip it.
        state.needsOnboarding = !!data?.isNewUser && !data?.hasApprovedBusiness;
        state.pendingPhone = null;
        state.otpCooldownSeconds = 30;
        state.sessionExpiry = expiresIn || null;

        if (state.needsOnboarding) {
          encryptedStorage.setItem('needsOnboarding', true);
        } else {
          encryptedStorage.removeItem('needsOnboarding');
        }
        encryptedStorage.removeItem('pendingPhone');

        if (accessToken) persistAccessToken(accessToken, expiresIn);
        if (refreshToken) persistRefreshToken(refreshToken);
        encryptedStorage.setItem('user', user);
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchProfile.fulfilled, (state, action) => {
        const data = action.payload?.data || action.payload;
        state.user = data;
        encryptedStorage.setItem(STORAGE_KEYS.USER, data);
      })
      .addCase(fetchMerchantProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMerchantProfile.fulfilled, (state, action) => {
        state.loading = false;
        if (!action.payload) return;

        const mapped = mapMerchantProfile(action.payload);
        state.user = {
          ...(state.user || {}),
          ...mapped,
        };
        encryptedStorage.setItem(STORAGE_KEYS.USER, state.user);
      })
      .addCase(fetchMerchantProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchAdminProfile.fulfilled, (state, action) => {
        if (!action.payload) return;

        const mapped = mapMerchantProfile(action.payload);
        state.user = {
          ...(state.user || {}),
          ...mapped,
          id: mapped.id || state.user?.id,
        };
        encryptedStorage.setItem('user', state.user);
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.mustChangePassword = false;
        state.needsOnboarding = false;
        state.pendingPhone = null;
        state.otpCooldownSeconds = 30;
        clearAccessToken();
        clearRefreshToken();
        encryptedStorage.removeItem('user');
        encryptedStorage.removeItem('mustChangePassword');
        encryptedStorage.removeItem('needsOnboarding');
        encryptedStorage.removeItem('pendingPhone');
        clearVerifyOtpSession();
      });
  },
});

export const {
  setTokens,
  setUser,
  setPendingPhone,
  clearPendingPhone,
  completeOnboarding,
  logout,
  clearAuthError,
  setSessionExpiry,
} = authSlice.actions;
export default authSlice.reducer;
